import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { getScaledImageSource, getImageSizeBasedOnViewport, imageSizes } from '@atc/images';
import { Link, Image, PositionedOverlay } from 'reaxl';
import { useDevice } from '@atc/react-device';
import { useBrand } from '@atc/brand';
import _isEqual from 'lodash/isEqual';
import clsx from 'clsx';
import { useFeatures } from '@atc/features';
import InventoryImagePlaceholder from '../InventoryImagePlaceholder/index.js';

/**
 *  The InventoryImage component generates a correctly formatted image element representing a piece of inventory.
 *  It will be properly scaled and sized based of its standard uiContext usage.  If a source is not passed to the
 *  component then a placeholder element is rendered.
 */
import { jsx as _jsx } from "react/jsx-runtime";
function InventoryImage(_ref) {
  let {
    alt,
    className,
    lazyLoad = true,
    linkProps = {},
    loading,
    render,
    sizes = {},
    src,
    uiContext = 'listing',
    ...rest
  } = _ref;
  const {
    isBrand
  } = useBrand();
  const isFord = isBrand('ford');
  const device = useDevice();
  const {
    image_optimization: [enableImageOptimization]
  } = useFeatures(['image_optimization']);
  const hasRenderFunction = typeof render === 'function';
  let imgSizes;

  // fix Ford brand does not have alpha and spotlight listing
  lazyLoad = lazyLoad || isFord && !src;
  if (sizes.width && sizes.height) {
    imgSizes = sizes;
  } else {
    switch (uiContext) {
      case 'alpha':
      case 'listing':
        imgSizes = getImageSizeBasedOnViewport({
          device,
          lazyLoad
        });
        break;
      case 'card':
      default:
        imgSizes = imageSizes.inventoryCard;
        break;
    }
  }
  const anchorProperties = {
    ...linkProps,
    'aria-label': linkProps['aria-label'] ?? alt
  };

  // If brand=ford, then do not use rel=nofollow for SEO purposes
  if (!isFord) {
    anchorProperties.rel = 'nofollow';
  }
  if (src) {
    const verifiedImgSrc = enableImageOptimization ? src.replace(/images.autotrader.com|assets.cai-media-management.com/, 'images2.autotrader.com') : src;
    const imgSrc = getScaledImageSource({
      src: verifiedImgSrc,
      ...imgSizes,
      isOptimized: enableImageOptimization
    });
    const imgProps = {
      className: clsx(className, {
        'center-block': uiContext === 'card',
        'img-vertically-aligned': uiContext !== 'card'
      }),
      'data-cmp': 'inventoryImage',
      lazyload: lazyLoad,
      loading,
      ...rest,
      ...imgSizes
    };
    imgProps.src = imgSrc;
    const imageStyles = {
      maxHeight: '100%',
      maxWidth: '100%',
      height: 'auto',
      lineHeight: '5em' // used only if image is broken
    };
    const image = /*#__PURE__*/_jsx(Image, {
      ...imgProps,
      // do not add alt if this is a link; link should have aria-label
      alt: linkProps.href ? '' : alt,
      style: imageStyles
    });
    if (hasRenderFunction) {
      return render({
        image
      });
    }

    // Wrap image in an 'a' tag if an href is provided by the parent component
    return linkProps.href ? /*#__PURE__*/React.createElement(Link, anchorProperties, image) : image;
  }
  const imgClassNames = clsx('img-vertically-aligned', className);
  const imagePlaceholder = /*#__PURE__*/_jsx(PositionedOverlay.Base, {
    className: clsx({
      'image-container': uiContext === 'listing' && !imgSizes?.minWidth
    }, 'bg-neutral-tertiary'),
    style: {
      height: imgSizes.height
    },
    children: /*#__PURE__*/_jsx(InventoryImagePlaceholder, {
      imgClassNames: imgClassNames,
      lazyload: true,
      ...imgSizes,
      ...rest
    })
  });
  if (hasRenderFunction) {
    return render({
      imagePlaceholder
    });
  }

  // Wrap image in an 'a' tag if an href is provided by the parent component
  return linkProps.href ? /*#__PURE__*/React.createElement(Link, anchorProperties, imagePlaceholder) : imagePlaceholder;
}
export default /*#__PURE__*/memo(InventoryImage, (oldProps, newProps) => _isEqual(oldProps, newProps));